/**
 * Qué clase de cuenta entró: el consultorio o quien se atiende.
 *
 * Ronda son dos paneles distintos sobre la misma marca. Cuál te toca no es una
 * pantalla que se elige cada vez, sino un dato de la cuenta: se decide al
 * crearla y desde entonces viaja con la sesión. El panel sólo lo lee.
 */
export type AccountRole = "medico" | "paciente";

export const DEFAULT_ACCOUNT_ROLE: AccountRole = "medico";

export function isAccountRole(value: unknown): value is AccountRole {
  return value === "medico" || value === "paciente";
}

/**
 * Normaliza lo que haya: una cuenta anterior a que esto existiera, un valor
 * que no reconocemos, o nada. Todo cae en médico, que es el panel que ya
 * funciona —mejor destino para un dato roto que una pantalla que no abre—.
 */
export function normalizeAccountRole(value: unknown): AccountRole {
  return isAccountRole(value) ? value : DEFAULT_ACCOUNT_ROLE;
}

/**
 * A quién le habló este navegador la última vez.
 *
 * La pantalla de ingreso tiene un problema de fondo: hasta que alguien no se
 * identifica, no hay forma de saber si mostrarle "tu espacio de trabajo" o
 * hablarle de sus turnos. El rol vive en la cuenta, y la cuenta se conoce
 * recién después de entrar.
 *
 * Esto lo resuelve por el lado práctico: al abrir sesión se anota de qué clase
 * era la cuenta, y la próxima visita al ingreso arranca con ese discurso. No
 * es una credencial ni decide permisos —sólo elige un texto—, así que que viva
 * en el navegador y se pueda editar a mano no rompe nada.
 */
const LAST_ROLE_KEY = "dd-last-account-role";

export function rememberAccountRole(role: AccountRole): void {
  try {
    globalThis.localStorage?.setItem(LAST_ROLE_KEY, role);
  } catch {
    // Modo privado, almacenamiento lleno o bloqueado: se pierde la comodidad
    // de recordar el discurso, no el ingreso. No hay nada que avisar.
  }
}

export function forgetAccountRole(): void {
  try {
    globalThis.localStorage?.removeItem(LAST_ROLE_KEY);
  } catch {
    // Ídem: no poder olvidar un texto no es un error que le importe a nadie.
  }
}

export function rememberedAccountRole(): AccountRole | null {
  try {
    const stored = globalThis.localStorage?.getItem(LAST_ROLE_KEY);
    return isAccountRole(stored) ? stored : null;
  } catch {
    return null;
  }
}
